import { Parameter } from "@/backend/models/parameter";
import { I18n } from "@/i18n/I18n";
import { ParameterInputType, ParameterSubjectType, ParameterType } from "@/types/parameterTypes";
import { useState, useEffect } from "react";
import { Button, Dropdown, Form, InputGroup, Modal } from "react-bootstrap";
import { Clipboard } from "lucide-react";
import { parameterTypeLabel } from "@/frontend/utils/parameterTypeLabel";
import { CustomToggle } from "../dropdown/CustomToggle";
import { CopyItem } from "../dropdown/CopyItem";
import { copyPasteTypes } from "@/types/copyPasteTypes";
import { CustomFieldInput } from "../customFIelds/CustomFieldInput";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { useGetRefAndParameterByConsumer, useParseProcedureReferences } from "@/frontend/hooks/parameters";
import { NonPrimitiveParameter } from "./NonPrimitiveParameter";
import { ParameterLink } from "./ParameterLink";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { useShallow } from "zustand/react/shallow";
import { CollapseButton } from "../buttons/CollapseButton";
import ValidationEdit from "../validations/ValidationEdit";
import { useReportStore } from "@/frontend/store/ReportStore";
import { ReportParams } from "@/types/reportTypes";
import { Link } from "react-router-dom";
import { BasicButton } from "../buttons/BasicButton";
import { CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { ChangeInputTypeItem } from "../dropdown/ChangeInputTypeItem";
import { ChangeTypeItem } from "../dropdown/ChangeTypeItem";
import { isEmbedApp } from "@/frontend/embed/embedApp";

export function ParameterItem(props: { parameter: Parameter }) {
  const { parameter } = props;
  const updateParameter = useParameterStore(state => state.updateParameter);
  const parameterIdentifierInvalid = useParameterStore(state => state.parameterIdentifierInvalid);
  const parseProcedureReferences = useParseProcedureReferences({ rangeParameterId: '', parentId: parameter.subjectId });
  const [identifier, setIdentifier] = useState(parameter.identifier);
  const [title, setTitle] = useState(parameter.title);
  const [titleError, setTitleError] = useState('');
  const [description, setDescription] = useState(parameter.description);
  const [showCollapsed, setShowCollapsed] = useState(false);

  const [identifierError, setIdentifierError] = useState('');
  const [validationTimeout, setValidationTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  const parameterLabel = `${I18n.get(parameter.type)} ${parameterTypeLabel(parameter)}`;

  const validations = useParameterStore(useShallow(state => state.getValidationForSubject(parameter.id)));
  const createValidationForParameter = useParameterStore(state => state.createValidationForParameter);
  const operation = useTreeStore(useShallow(state => state.operations.find( op => op.id === parameter.subjectId)));

  useEffect(() => {
    setIdentifier(parameter.identifier);
  }, [parameter.identifier]);

  useEffect(() => {
    setTitle(parameter.title);
  }, [parameter.title]);

  const onChangeTitle = (value: string) => {
    setTitle(value);
    if (!value) {
      setTitleError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      return;
    }

    setTitleError(parseProcedureReferences(value) || '');
  };

  const onBlurTitle = async () => {
    if (titleError || !title) return;
    void updateParameter({ parameter: { ...parameter, title }, hideToast: true });
  };

  const onBlurDescription = async () => {
    void updateParameter({ parameter: { ...parameter, description }, hideToast: true });
  };

  const onUpdateIdentifier = async (value: string) => {
    const response = await updateParameter({ parameter: { ...parameter, identifier: value }, hideToast: true });
    if (response.errors && response.errors.length > 0) {
      setIdentifierError(response.errors[0].message);
      return response.errors[0].message;
    }
    setIdentifierError('');
    return null;
  };

  const onBlurIdentifier = async () => {
    if ((identifierError || !identifier) && parameter.subjectType === ParameterSubjectType.OPERATION) return;
    const updateResult = await onUpdateIdentifier(identifier);
    if (updateResult) {
      setIdentifierError('');
      setIdentifier(parameter.identifier);
    }
  };

  const onChangeIdentifier = (value: string) => {
    setIdentifierError('');
    setIdentifier(value);

    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    const timeout = setTimeout(() => {
      if (!value && parameter.subjectType === ParameterSubjectType.OPERATION) {
        setIdentifierError(I18n.get('requiredFieldError', { field: I18n.get('identifier') }));
      }
      const validationError = parameterIdentifierInvalid(new Parameter({ ...parameter, identifier: value }));
      if (validationError) {
        setIdentifierError(validationError);
      }
    }, 300);

    setValidationTimeout(timeout);
  };

  const updateViewOnly = (e: React.ChangeEvent<HTMLInputElement>) => {
    void updateParameter({ parameter: { ...parameter, viewOnly: e.target.checked } });
  };

  const canAddValidation = parameter.isValidatable();
  const boilerplate = codeBoilerplate(parameter, identifier);

  return (
    <div className='card bg-light mb-2' id={parameter.id}>
      <div className='card-body'>
        <div className='d-flex justify-content-between'>
          <small className='text-muted text-uppercase'>{parameterLabel}</small>
          <Dropdown drop='down'>
            <Dropdown.Toggle size='sm' as={CustomToggle(I18n.get('parameter'))} variant='dark' id="dropdown-basic" />
            <Dropdown.Menu>
              <DeleteParameterItem parameter={parameter} />
              <CopyItem instance={copyPasteTypes.Parameter} itemId={parameter.id} modelLabel={I18n.get(parameter.type)} />
              <ChangeInputTypeItem parameter={parameter} />
              <ChangeTypeItem parameter={parameter} />
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className='parameter-row-fields'>
          <div className='parameter-row-title'>
            <Form.Label className="col-form-label col-form-label-sm">{I18n.get('title')}</Form.Label>
            <Form.Control
              size='sm'
              aria-label={I18n.get('enterField', { field: I18n.get('title') })}
              value={title}
              isInvalid={!!titleError}
              onChange={(event) => onChangeTitle(event.target.value)}
              onBlur={() => void onBlurTitle()}
            />
            <Form.Control.Feedback type="invalid">
              { titleError }
            </Form.Control.Feedback>
          </div>
          <div className='parameter-row-identifier'>
            <Form.Label className="col-form-label col-form-label-sm">{I18n.get('identifier')}</Form.Label>
            <InputGroup size='sm' hasValidation>
              <Form.Control
                size='sm'
                aria-label={I18n.get('enterField', { field: I18n.get('identifier') })}
                value={identifier}
                isInvalid={!!identifierError}
                onChange={(event) => onChangeIdentifier(event.target.value)}
                onBlur={() => void onBlurIdentifier()}
              />
              { boilerplate !== null &&
                <Button
                  variant='outline-secondary'
                  className='d-flex align-items-center'
                  disabled={!!identifierError || !identifier}
                  onClick={() => void navigator.clipboard.writeText(boilerplate)}
                  title={I18n.get('copyParamBoilerplate')}
                  aria-label={I18n.get('copyParamBoilerplate')}
                >
                  <Clipboard size={14} />
                </Button> }
              <Form.Control.Feedback type="invalid">
                { identifierError }
              </Form.Control.Feedback>
            </InputGroup>
          </div>

          <div className='parameter-row-link'>
            <ParameterLink parameter={parameter} operation={operation} />
          </div>
        </div>
        <div className="border rounded bg-light mt-2">
          <div className='d-flex align-items-center'>
            <CollapseButton showElement={showCollapsed} setShowElement={setShowCollapsed} title={parameterLabel} />
            <span className="fw-semibold text-muted">{I18n.get('details')}</span>
          </div>
          {showCollapsed && (
            <div className='px-2'>
              <Form.Label className="col-form-label col-form-label-sm">{I18n.get('description')}</Form.Label>
              <Form.Control
                as='textarea'
                aria-label={I18n.get('enterField', { field: I18n.get('description') })}
                defaultValue={parameter.description}
                onChange={(event) => setDescription(event.target.value)}
                onBlur={() => void onBlurDescription()}
              />
              <ParameterValue parameter={parameter} />
              { parameter.subjectType === ParameterSubjectType.PROCEDURE &&
                <div className='d-flex gap-2 align-items-center'>
                  <Form.Label className="col-form-label col-form-label-sm">{I18n.get('viewOnly')}:</Form.Label>
                  <Form.Check type="checkbox" aria-label={I18n.get('setViewOnly')} checked={parameter.viewOnly} onChange={updateViewOnly} />
                </div>
              }
              { parameter.inputType === ParameterInputType.MULTIPLE_CHOICE && <div className='d-flex gap-2 align-items-center'>
                <Form.Label className="col-form-label col-form-label-sm">{I18n.get('allowCustomChoices')}:</Form.Label>
                <Form.Check
                  type="checkbox"
                  aria-label={I18n.get('allowCustomChoices')}
                  checked={parameter.allowCustomChoices}
                  onChange={(e) => void updateParameter({ parameter: { ...parameter, allowCustomChoices: e.target.checked } })}
                />
              </div> }
              { validations.length > 0 &&
                <div className='py-1'>
                  <Form.Label className="col-form-label col-form-label-sm">{I18n.get('validations')}:</Form.Label>
                  <div className='d-flex flex-column gap-1'>
                    { validations.map(validation => <ValidationEdit key={validation.id} validation={validation} owningParameter={parameter} />) }
                  </div>
                </div>
              }
                { canAddValidation && <button type='button' aria-label={I18n.get('addValidation')} className='btn btn-sm btn-primary mb-2 mt-2' onClick={() => void createValidationForParameter(parameter.id)}>{I18n.get('addValidation')}</button > }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// the line a code operation or validation script needs to read this input or write this output
function codeBoilerplate(parameter: Parameter, identifier: string): string | null {
  if (parameter.subjectType !== ParameterSubjectType.OPERATION && parameter.subjectType !== ParameterSubjectType.VALIDATION) {
    return null;
  }
  if (parameter.type === ParameterType.INPUT) {
    return `${identifier} = v.get_input("${identifier}")`;
  }
  if (parameter.type === ParameterType.OUTPUT) {
    return `v.set_output("${identifier}", <placeholder>)`;
  }
  return null;
}

export function ParameterValue(props: { parameter: Parameter }) {
  const { parameter } = props;
  const step = useTreeStore(useShallow(state => state.containerLookup[parameter.subjectId]));
  const consumerReference = useGetRefAndParameterByConsumer(parameter.id);

  if(step?.container.isRepeatingStep() && parameter.inputType === ParameterInputType.LIST && parameter.fieldType !== CUSTOM_FIELD_TYPES.Object) {
    return null;
  }

  if (parameter.isPrimitive()) {
    return <CustomFieldInput customField={parameter.fields[0]} label={I18n.get('defaultValue')} isEditView readOnly={!!consumerReference.currentReference} />;
  }

  return <NonPrimitiveParameter parameter={parameter} disabled={!!consumerReference.currentReference && !consumerReference.currentReference.consumerObjectKeyId} runView={false} />;
}

function DeleteParameterItem(props: { parameter: Parameter }) {
  const { parameter } = props;
  const [showModal, setShowModal] = useState(false);
  const deleteParameter = useParameterStore(state => state.deleteParameter);

  return (
    <>
      <Dropdown.Item className='text-danger' onClick={() => setShowModal(true)}>
        {I18n.get('delete')}
      </Dropdown.Item>
      {showModal && (
        <Modal show onHide={() => setShowModal(false)} centered>
          <Modal.Header className='bg-danger text-white' closeButton>
            <Modal.Title>{I18n.get('delete')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-flex flex-column">
            <LinkList parameter={parameter} />
              <p style={{whiteSpace: 'pre-wrap'}} className="d-flex pb-3">
                {I18n.get('areYouSure', { model: I18n.get(parameter.type), title: parameter.title })}
              </p>
            <div className="d-flex justify-content-end gap-2">
              <BasicButton className="btn btn-secondary" onClick={() => setShowModal(false)} label={I18n.get('cancel')} />
              <BasicButton className='btn btn-primary' onClick={() => deleteParameter({ id: parameter.id })} label={I18n.get('confirm')} /> 
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}

function LinkList(props: { parameter: Parameter }) {
  const { parameter } = props;
  const readReportsBySourceId = useReportStore(state => state.readReportsBySourceId);
  const [reports, setReports] = useState<ReportParams[]>([]);

  useEffect(() => {
    const setConfirmDeleteMessage = async () => {
      const reply = await readReportsBySourceId(parameter.id);
      setReports(reply.reports);
    };
    void setConfirmDeleteMessage();
  }, []);

  if (reports.length === 0) {
    return null;
  }

  return (
    <div className="mb-3">
      <h6>{I18n.get('outputUsedInReports')}</h6>
      <ul className="list-unstyled">
        {reports.map((report) => (
          <li key={report.id}>
            {/* The report editor is only served by the embed app. */}
            {isEmbedApp() ? (
              <Link to={`/reports/edit/${report.id}`} className="text-decoration-none">
                • {report.title}
              </Link>
            ) : (
              <>• {report.title}</>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
